import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token') || sessionStorage.getItem('token'));

  useEffect(() => {
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      validateToken(storedToken);
    } else {
      setLoading(false);
    }
    const handleAuthLogout = () => {
      const hash = window.location.hash;
      const path = hash.replace('#', '') || window.location.pathname;
      const isAuthPage = path.startsWith('/auth/') || path === '/admin/login' ||
                         path === '/admin' || path === '/role-selection' ||
                         path === '/forgot-password' || path === '/reset-password';
      if (isAuthPage) {
        clearToken();
      } else {
        logout();
      }
    };
    window.addEventListener('auth_logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth_logout', handleAuthLogout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAuthRoute = () => {
    const hash = window.location.hash;
    const path = hash.replace('#', '') || window.location.pathname;
    return path.startsWith('/auth/') || path.startsWith('/admin/login') || 
           path === '/admin' || path === '/role-selection' ||
           path === '/forgot-password' || path === '/reset-password';
  };

  const clearToken = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    setUser(null);
    setToken(null);
  };

  const validateToken = async (tokenToValidate) => {
    try {
      const res = await authAPI.getProfile();
      if (res.data?.success && res.data?.data) {
        setUser(res.data.data);
      } else {
        console.warn('⚠️ Token validation failed (no data):', res.data);
        if (res.data?.success === false) {
          if (isAuthRoute()) { clearToken(); } else { logout(); }
        }
      }
    } catch (error) {
      console.error('❌ Token validation system error:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (isAuthRoute()) { clearToken(); } else { logout(); }
      }
    } finally {
      setLoading(false);
    }
  };

  // Dedicated function to refresh user profile (e.g., after subscription or approval)
  const refreshProfile = async () => {
    try {
      if (!token) return;
      const res = await authAPI.getProfile();
      if (res.data?.success && res.data?.data) {
        setUser(res.data.data);
        localStorage.setItem('user', JSON.stringify(res.data.data));
      }

    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  };

  const saveToken = (newToken) => {
    if (newToken && typeof newToken === 'string' && newToken.length > 10) {
      localStorage.setItem('token', newToken);
    } else {
      localStorage.removeItem('token');
    }
  };

  const login = async (role, email, password, rememberMe = false, additionalData = {}) => {
    try {
      const res = await authAPI.login(role, { email, password, ...additionalData });
      const data = res.data;
      if (data.success && data.data) {
        const { token: newToken, user: userData } = data.data;

        if (rememberMe) {
          saveToken(newToken);
        } else {
          sessionStorage.setItem('token', newToken && typeof newToken === 'string' && newToken.length > 10 ? newToken : '');
        }

        setToken(newToken);
        setUser(userData);

        if (userData && typeof userData === 'object') {
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userRole', userData.role || '');
        }

        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) {
          authAPI.saveDeviceToken(fcmToken).catch(e => console.log('Device token save ignored for now', e));
        }
        const voipToken = localStorage.getItem('voip_token');
        if (voipToken && authAPI.saveVoipToken) {
          authAPI.saveVoipToken(voipToken).catch(e => console.log('VoIP token save ignored for now', e));
        }

        return { success: true, user: userData };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, message };
    }
  };

  const setAuthFromCodeExchange = async ({ code, redirect_uri, role }) => {
    try {
      const res = await authAPI.googleExchange({ code, redirect_uri, role: role || 'patient' });
      const data = res.data;
      if (data.success && data.data) {
        const { token: newToken, user: userData } = data.data;
        saveToken(newToken);
        setToken(newToken);
        setUser(userData);
        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) authAPI.saveDeviceToken(fcmToken).catch(() => {});
        return { success: true, user: userData };
      }
      return { success: false, message: data.message || 'Exchange failed' };
    } catch (error) {
      console.error('Google code exchange error:', error);
      return { success: false, message: error.response?.data?.message || 'Google sign-in failed' };
    }
  };

  const googleLogin = async (tokenId, role) => {
    try {
      const res = await authAPI.googleLogin({ tokenId, role });
      const data = res.data;
      if (data.success && data.data) {
        const { token: newToken, user: userData } = data.data;
        saveToken(newToken);
        if (userData && typeof userData === 'object') {
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userRole', userData.role || '');
        }
        setToken(newToken);
        setUser(userData);
        
        // Register Push Tokens if available
        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) {
          authAPI.saveDeviceToken(fcmToken).catch(e => console.log('Device token save ignored for now', e));
        }
        const voipToken = localStorage.getItem('voip_token');
        if (voipToken && authAPI.saveVoipToken) {
          authAPI.saveVoipToken(voipToken).catch(e => console.log('VoIP token save ignored for now', e));
        }

        return { success: true, user: userData };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (error) {
      console.error('Google login error:', error);
      const message = error.response?.data?.message || 'Google login failed';
      return { success: false, message };
    }
  };

  const register = async (role, userData) => {
    try {
      const res = await authAPI.register(role, userData);
      const data = res.data;
      if (data.success && data.data) {
        const { token: newToken, user: userData } = data.data;
        if (newToken) {
          saveToken(newToken);
          setToken(newToken);
        }
        setUser(userData);
        if (userData && typeof userData === 'object') {
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userRole', userData.role || '');
        }
        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) authAPI.saveDeviceToken(fcmToken).catch(() => {});
        return { success: true, user: userData };
      }
      return { success: false, message: data.message || 'Registration failed' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: error.response?.data?.message || 'Registration failed' };
    }
  };

  const appleLogin = async ({ identityToken, email, name, role }) => {
    try {
      const res = await authAPI.appleLogin({ identityToken, email, name, role });
      const data = res.data;
      if (data.success && data.data) {
        const { token: newToken, user: userData } = data.data;
        saveToken(newToken);
        setToken(newToken);
        setUser(userData);

        if (userData && typeof userData === 'object') {
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userRole', userData.role || '');
        }

        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) {
          authAPI.saveDeviceToken(fcmToken).catch(e => console.log('Device token save ignored for now', e));
        }
        const voipToken = localStorage.getItem('voip_token');
        if (voipToken && authAPI.saveVoipToken) {
          authAPI.saveVoipToken(voipToken).catch(e => console.log('VoIP token save ignored for now', e));
        }

        return { success: true, user: userData };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (error) {
      console.error('Apple login error:', error);
      const message = error.response?.data?.message || 'Apple login failed';
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout();
      }
    } catch (e) {
      console.error('API logout error', e);
    }
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('pending_notif_consultation');
    // We intentionally DO NOT clear fcm_token or voip_token from localStorage on logout, 
    // simply so the next person who logs into this physical phone immediately sends the active token 
    // to their own new profile.
    setUser(null);
    setToken(null);
    navigate('/role-selection', { replace: true });
  };

  const getRedirectPath = (targetUser) => {
    if (!targetUser) return '/login';
    if (targetUser.role === 'admin') return '/admin/dashboard';
    if (targetUser.role === 'patient') return '/patient/home';

    // Priority 1: Subscription
    if (targetUser.subscription_status !== 'active') {
      return `/subscription?role=${targetUser.role}`;
    }

    // Priority 2: Profile Image (doctors only)
    if (targetUser.role === 'doctor' && !targetUser.profile_image) {
      return '/onboarding/profile';
    }

    // Priority 3: Document Upload
    // Skip for hospital-affiliated doctors
    if (!targetUser.documents_uploaded && !targetUser.hospital_id) {
      return '/onboarding/documents';
    }

    // Priority 3: Admin Approval
    if (!targetUser.is_approved) {
      return '/onboarding/pending';
    }

    // Fully approved
    return `/${targetUser.role}/dashboard`;
  };

  const value = {
    user,
    login,
    register,
    googleLogin,
    setAuthFromCodeExchange,
    appleLogin,
    logout,
    getRedirectPath,
    refreshUser: refreshProfile,
    refreshProfile,
    token,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
